import React from "react";
import classnames from "classnames";
import { getRoleLabel } from "lib/role_helper";
import { Rune, StatShard } from "@outplayed/tooltips";
import { getRiotAssetsContext } from "@outplayed/riot-assets";

const parseText = (text, currentTextIndex, textSubMatches, props) => {
  const { getChampionName, getChampionImg, allKeystones, seoNames, role } = props;

  if (!text) {
    return [];
  } else if (textSubMatches.length === 0) {
    return [text];
  }

  const textType = textSubMatches[0].type;
  const matchIndex = textSubMatches[0].match.index;
  const matchText = textSubMatches[0].match[0];
  const matchTextLength = textSubMatches[0].match[0].length;

  if (matchIndex - currentTextIndex !== 0) {
    return [text.slice(0, matchIndex - currentTextIndex)].concat(
      parseText(text.slice(matchIndex - currentTextIndex), matchIndex, textSubMatches, props),
    );
  } else {
    let textSub = "";
    if (textType === "rune") {
      const runeId = matchText.replace(/(\[rune_)|\]/g, "");
      const isKeystone = allKeystones && allKeystones.includes(Number(runeId));
      textSub = (
        <Rune key={currentTextIndex} runeId={runeId} ssr>
          {({ imgSrc, runeName }) => {
            return (
              <div className="text-sub text-sub_rune">
                <div className={classnames("rune-img", { "rune-img_keystone": isKeystone })}>
                  <img src={imgSrc} />
                </div>
                <span>{runeName}</span>
              </div>
            );
          }}
        </Rune>
      );
    } else if (textType === "shard") {
      const shardText = matchText.replace(/\[|\]/g, "");
      const textSplit = shardText.split("_");
      const shardId = textSplit[1];
      const shardRow = textSplit[2];
      textSub = (
        <StatShard key={currentTextIndex} shardRow={shardRow} shardId={shardId} ssr>
          {({ imgSrc, shardName }) => {
            return (
              <div className="text-sub text-sub_shard">
                <div className={classnames("shard-img")}>
                  <img src={imgSrc} />
                </div>
                <span>
                  {shardName}
                  {props.displayShardRowText && shardRow ? ` (${shardRow})` : ""}
                </span>
              </div>
            );
          }}
        </StatShard>
      );
    } else if (textType === "champion" || textType === "champion2") {
      textSub = seoNames.name;
      if (textType === "champion2" && seoNames.altName) {
        textSub = seoNames.altName;
      }
    } else if (textType === "oppChampion") {
      const championName = getChampionName(props.oppChampionId);
      if (championName) {
        textSub = (
          <div key={currentTextIndex} className="text-sub text-sub_opp-champion_with-image">
            <img src={getChampionImg(props.oppChampionId)} />
            <span>{championName}</span>
          </div>
        );
      } else {
        textSub = (
          <span key={currentTextIndex} className="text-sub text-sub_opp-champion">
            All Champions
          </span>
        );
      }
    } else if (textType === "role") {
      textSub = getRoleLabel(role);
    }

    return [textSub].concat(
      parseText(text.slice(matchTextLength), currentTextIndex + matchTextLength, textSubMatches.slice(1), props),
    );
  }
  return [text];
};

const getTextSubMatches = (text) => {
  const regexps = [
    { type: "rune", regexp: /\[rune_[0-9]+\]/g },
    { type: "shard", regexp: /\[shard_[0-9]+(_[1-9])?\]/g },
    { type: "champion", regexp: /\[champion\]/g },
    { type: "champion2", regexp: /\[champion2\]/g },
    { type: "role", regexp: /\[role\]/g },
    { type: "oppChampion", regexp: /\[oppChampion\]/g },
  ];
  const allMatches = [];
  for (const i of regexps) {
    const newRegExp = new RegExp(i.regexp);
    const matches = text.matchAll(newRegExp);
    for (const match of matches) {
      allMatches.push({ type: i.type, match });
    }
  }
  allMatches.sort((a, b) => a.match.index - b.match.index);

  return allMatches;
};

export const useGenerateParsedText = () => {
  const { getChampionName, getChampionImg } = getRiotAssetsContext();
  return (text, props) => {
    const textSubMatches = getTextSubMatches(text);
    return parseText(text, 0, textSubMatches, { ...props, getChampionName, getChampionImg });
  };
};
